import React, { useState, useEffect, useRef } from 'react';

// mui
import {
    Typography,
    Button,
    Box,
    Grid,
    TextField,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    FormControlLabel,
    Checkbox,
} from '@mui/material';

// api
import { MfaSetup, MfaVerifySetup, MfaDisable, MfaStatus } from '../../api';

function SettingsAccountMfa({ onBack }) {
    const [mfaStatus, setMfaStatus] = useState({ enabled: false, backupCodesRemaining: 0 });
    const [setupStep, setSetupStep] = useState(0); // 0: status, 1: show QR, 2: verify
    const [qrCode, setQrCode] = useState('');
    const [secret, setSecret] = useState('');
    const [backupCodes, setBackupCodes] = useState([]);
    const [disableDialogOpen, setDisableDialogOpen] = useState(false);
    const [disableUseBackupCode, setDisableUseBackupCode] = useState(false);
    const codeRef = useRef();
    const disableCodeRef = useRef();

    useEffect(() => {
        loadMfaStatus();
    }, []);

    const loadMfaStatus = () => {
        MfaStatus((data) => {
            setMfaStatus(data);
        });
    };

    const onClickEnableMfa = () => {
        MfaSetup((data) => {
            setQrCode(data.qrCode);
            setSecret(data.secret);
            setBackupCodes(data.backupCodes);
            setSetupStep(1);
        });
    };

    const onClickNextToVerify = () => {
        setSetupStep(2);
    };

    const onClickVerifySetup = () => {
        const code = codeRef.current.value;
        if (!code || code.length !== 6) {
            return;
        }

        MfaVerifySetup(code, (data) => {
            onBack?.();
        });
    };

    const onClickDisableMfa = () => {
        setDisableUseBackupCode(false);
        setDisableDialogOpen(true);
    };

    const onCloseDisableDialog = () => {
        setDisableDialogOpen(false);
    };

    const onConfirmDisableMfa = () => {
        const code = disableCodeRef.current?.value;
        if (!code) return;

        MfaDisable(code, disableUseBackupCode, (data) => {
            setDisableDialogOpen(false);
            loadMfaStatus();
        });
    };

    const onClickCancel = () => {
        setSetupStep(0);
        setQrCode('');
        setSecret('');
        setBackupCodes([]);
    };

    return (
        <Box
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
            <Dialog
                open={disableDialogOpen}
                onClose={onCloseDisableDialog}
                slotProps={{ paper: { elevation: 0, variant: 'outlined' } }}>
                <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Enter your current authentication code to confirm.
                    </DialogContentText>
                    <TextField
                        fullWidth
                        autoFocus
                        label={disableUseBackupCode ? 'Backup Code' : 'Authentication Code'}
                        inputRef={disableCodeRef}
                        slotProps={{
                            htmlInput: {
                                maxLength: disableUseBackupCode ? 8 : 6,
                                style: {
                                    textAlign: 'center',
                                    fontSize: '1.2rem',
                                    letterSpacing: '0.3em',
                                },
                            },
                        }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={disableUseBackupCode}
                                onChange={(e) => setDisableUseBackupCode(e.target.checked)}
                            />
                        }
                        label='Use backup code'
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center' }}>
                    <Button variant='outlined' onClick={onCloseDisableDialog}>
                        Cancel
                    </Button>
                    <Button variant='contained' color='error' onClick={onConfirmDisableMfa}>
                        Disable
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Step 0: Status view */}
            {setupStep === 0 && (
                <Grid container spacing={0} direction='column' justifyContent='center' alignItems='center'>
                    {mfaStatus.enabled ? (
                        <Typography variant='m_titleS' color='primary.main' sx={{ my: 2 }}>
                            Disable Two-Factor Authentication
                        </Typography>
                    ) : (
                        <Typography variant='m_titleS' color='primary.main' sx={{ my: 2 }}>
                            Enable Two-Factor Authentication
                        </Typography>
                    )}
                    {mfaStatus.enabled && (
                        <Typography variant='m_overline' color='primary.main' sx={{ my: 2 }}>
                            Backup codes remaining: {mfaStatus.backupCodesRemaining}
                        </Typography>
                    )}
                    {mfaStatus.enabled ? (
                        <Button variant='contained' color='error' onClick={onClickDisableMfa} sx={{ my: 2 }}>
                            Disable
                        </Button>
                    ) : (
                        <Button variant='contained' onClick={onClickEnableMfa} sx={{ my: 2 }}>
                            Enable
                        </Button>
                    )}
                </Grid>
            )}

            {/* Step 1: Show QR code and backup codes */}
            {setupStep === 1 && (
                <Box>
                    <Typography variant='m_titleS' color='primary.main'>
                        Enable Two-Factor Authentication
                    </Typography>
                    <Typography variant='body2' sx={{ my: 2 }}>
                        Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                    </Typography>
                    {qrCode && (
                        <Box sx={{ mb: 2 }}>
                            <img src={qrCode} alt='QR Code' style={{ maxWidth: '100%', height: 'auto' }} />
                        </Box>
                    )}

                    <Typography variant='body2' sx={{ mb: 1 }}>
                        Or enter this code manually:
                    </Typography>
                    <TextField fullWidth value={secret} slotProps={{ input: { readOnly: true } }} sx={{ mb: 3 }} />

                    <Box sx={{ mb: 2 }}>
                        <Typography variant='subtitle2' sx={{ mb: 1 }}>
                            Backup Codes
                        </Typography>
                        <Typography variant='body2' sx={{ mb: 1 }}>
                            Save these backup codes in a safe place. You can use them to access your account if you lose
                            your authenticator app.
                        </Typography>
                        <Box sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                            {backupCodes.map((code, index) => (
                                <div key={index}>{code}</div>
                            ))}
                        </Box>
                    </Box>

                    <Stack direction='row' spacing={2}>
                        <Button onClick={onClickCancel} variant='outlined'>
                            Cancel
                        </Button>
                        <Button onClick={onClickNextToVerify} variant='contained'>
                            Next
                        </Button>
                    </Stack>
                </Box>
            )}

            {/* Step 2: Verify code */}
            {setupStep === 2 && (
                <Box>
                    <Typography variant='m_titleS' color='primary.main'>
                        Verify Setup
                    </Typography>
                    <Typography variant='body2' sx={{ my: 2 }}>
                        Enter the 6-digit code from your authenticator app to complete setup.
                    </Typography>
                    <TextField
                        fullWidth
                        autoFocus
                        label='Verification Code'
                        inputRef={codeRef}
                        slotProps={{
                            htmlInput: {
                                maxLength: 6,
                                style: {
                                    textAlign: 'center',
                                    fontSize: '1.5rem',
                                    letterSpacing: '0.5em',
                                },
                            },
                        }}
                        sx={{ my: 2 }}
                    />
                    <Stack direction='row' spacing={2}>
                        <Button onClick={onClickCancel} variant='outlined'>
                            Cancel
                        </Button>
                        <Button onClick={onClickVerifySetup} variant='contained'>
                            Verify & Enable
                        </Button>
                    </Stack>
                </Box>
            )}
        </Box>
    );
}

export { SettingsAccountMfa };
